import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Location} from '../../../types/location.type';
import {IThresholdLevel, LocationDataProcessMeasure, ThresholdLevel} from '../../../classes/offset/LocationDataProcessMeasure';
import {ActivatedRoute} from '@angular/router';
import {ProjectMeasureService} from '../../../services/project-measure/project-measure.service';
import {ProjectService} from '../../../services/project/project.service';
import {TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {faTrash, faPen} from '@fortawesome/free-solid-svg-icons';
import {LocalStorageViewsService} from '../../../services/local-storage-views/local-storage-views.service';
import {FileReaderService} from '../../../services/file-reader/file-reader.service';
import saveAs from 'file-saver';
import {faDownload} from '@fortawesome/free-solid-svg-icons/faDownload';
import {cloneDeep} from 'lodash';
import * as moment from 'moment';
import {LocationsFormatterService} from '../../../services/project-measure/locations-formatter.service';

@Component({
  selector: 'app-threshold-form',
  templateUrl: './threshold-form.component.html',
  styleUrls: ['./threshold-form.component.css']
})
export class ThresholdFormComponent implements OnInit {
  @ViewChild('file') fileInput: ElementRef;


  @Output() showSpinner: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input() selectedLocation: Location.serverSide;
  @Input() locations: Location.clientSide[];
  @Input() dateStart: Date;
  @Input() dateEnd: Date;

  @Input() set thresholdTable(value: LocationDataProcessMeasure[]) {
    this._thresholdTable = value || [];
    this.updateThresholdsDisplayDates();
  }

  get thresholdTable() {
    return this._thresholdTable;
  }

  @Output() thresholdTableChange = new EventEmitter<LocationDataProcessMeasure[]>();

  @Input() idProject: number;

  get selectedLocationThresholdTable(): LocationDataProcessMeasure[] {
    return this._thresholdTable.filter((threshold) => this.selectedLocation.id === threshold.project_device_location);
  }

  get isSelectedLocationValid(): boolean {
    return !!this.selectedLocation['device_type'];
  }

  faDownload = faDownload;
  private _thresholdTable: LocationDataProcessMeasure[];
  public selectedFile: File;
  faPen = faPen;
  faTrash = faTrash;
  disabled = false;
  dateStartPicker: Date;
  timeStartPicker: {
    hour: number;
    minute: number;
    second: number;
  };
  dateEndPicker: Date;
  timeEndPicker: {
    hour: number;
    minute: number;
    second: number;
  };

  thresholdValue: number;
  thresholdLevel: IThresholdLevel;
  thresholdLevels: IThresholdLevel[] = [];

  dateStartPickerThreshold: Date;
  timeStartPickerThreshold: {
    hour: number;
    minute: number;
    second: number;
  };
  dateEndPickerThreshold: Date;
  timeEndPickerThreshold: {
    hour: number;
    minute: number;
    second: number;
  };

  compareCurrentThresholdLevel(o1: IThresholdLevel, o2: IThresholdLevel): boolean {
    return o1.id === o2.id;
  }

  constructor(private route: ActivatedRoute,
              private projectMeasureService: ProjectMeasureService,
              private projectService: ProjectService,
              private translateService: TranslateService,
              private toastr: ToastrService,
              private localStorageService: LocalStorageViewsService,
              private fileService: FileReaderService,
              private locationsFormatterService: LocationsFormatterService
  ) {
  }

  ngOnInit() {
    this.EnumThresholdLevelToArray();
    this.initDateTimePickers(this.dateStart, this.dateEnd);
  }

  private updateThresholdsDisplayDates() {
    const thresholds = this.thresholdTable;
    for (const threshold of thresholds) {
      this.updateThresholdDateDisplay(threshold);
    }
  }

  private updateThresholdDateDisplay(threshold: LocationDataProcessMeasure) {
    const dateStartObject = moment(threshold.start_date).utc(true).clone();
    const dateEndObject = moment(threshold.end_date).utc(true).clone();
    threshold['dateStartToDisplay'] = this.projectMeasureService.formatDateToDisplay(dateStartObject.toISOString());
    threshold['dateEndToDisplay'] = this.projectMeasureService.formatDateToDisplay(dateEndObject.toISOString());
  }

  private initDateTimePickers(dateStart, dateEnd) {
    this.dateStartPicker = this.projectMeasureService.initDatePicker(dateStart);
    this.timeStartPicker = this.projectMeasureService.initTimePicker(dateStart);
    this.dateEndPicker = this.projectMeasureService.initDatePicker(dateEnd);
    this.timeEndPicker = this.projectMeasureService.initTimePicker(dateEnd);
  }


  deleteThreshold(thresholdId: string) {
    this._thresholdTable = this._thresholdTable.filter((threshold) => threshold.id !== thresholdId);
    this.saveThresholdTable();
  }

  onPatchClick(threshold) {
    const patchedThreshold = this.patchThreshold(threshold);
    if (!!patchedThreshold) {
      this._thresholdTable = this._thresholdTable.filter((t) => t.id !== threshold.id);
      this._thresholdTable.push(patchedThreshold);
      this.saveThresholdTable();
    } else {
      this.toastr.error(this.translateService.instant('START_DATE_AFTER_END_DATE'));
    }
    return;
  }

  patchThreshold(threshold: LocationDataProcessMeasure) {
    const startDate = this.projectMeasureService.formatDateToString(this.timeStartPickerThreshold, this.dateStartPickerThreshold);
    const endDate = this.projectMeasureService.formatDateToString(this.timeEndPickerThreshold, this.dateEndPickerThreshold);
    if (startDate > endDate) {
      return;
    } else {
      threshold.selected = false;
      const newThreshold = {...threshold};
      newThreshold.start_date = startDate;
      newThreshold.end_date = endDate;
      newThreshold.selected = false;
      newThreshold.id = this.generateThresholdId(startDate, endDate);
      return newThreshold;
    }
  }

  EnumThresholdLevelToArray() {
    const result: { id: string; name: string }[] = [];

    for (const level in ThresholdLevel) {
      if (typeof ThresholdLevel[level] === 'string') {
        result.push({id: level, name: ThresholdLevel[level]});
      }
    }
    this.thresholdLevels = result;
  }

  onPatchThresholdButtonClick(thresholdToPatch: LocationDataProcessMeasure) {
    for (const threshold of this._thresholdTable) {

      threshold.selected = (thresholdToPatch.id === threshold.id);
    }
    const dateStartObject = moment(thresholdToPatch.start_date).utc(true).clone();
    this.dateStartPickerThreshold = dateStartObject.toDate();
    this.timeStartPickerThreshold = {
      hour: dateStartObject.get('hour'),
      minute: dateStartObject.get('minute'),
      second: dateStartObject.get('second'),
    };
    const dateEndObject = moment(thresholdToPatch.end_date).utc(true).clone();
    this.dateEndPickerThreshold = dateEndObject.toDate();
    this.timeEndPickerThreshold = {
      hour: dateEndObject.get('hour'),
      minute: dateEndObject.get('minute'),
      second: dateEndObject.get('second'),
    };

    this.updateThresholdDateDisplay(thresholdToPatch);
  }


  submitThreshold() {
    if (Object.getOwnPropertyNames(this.selectedLocation).length === 0) {
      alert(this.translateService.instant('PLEASE_LOCATION'));
      return;
    }
    if (this.thresholdLevel === undefined) {
      this.toastr.error(this.translateService.instant('MUST_SELECT_LEVEL'));
      return;
    }
    if (this.thresholdValue === undefined) {
      this.toastr.error(this.translateService.instant('MUST_CHOOSE_VALUE'));
      return;
    }
    const start_date: string = moment(this.dateStartPicker).clone().set(this.timeStartPicker).format();
    const end_date: string = moment(this.dateEndPicker).clone().set(this.timeEndPicker).format();
    if (start_date === 'Invalid date' || end_date === 'Invalid date') {
      this.toastr.error(this.translateService.instant('INVALID_DATES'));
      return;
    }
    if (start_date > end_date) {
      this.toastr.error(this.translateService.instant('START_DATE_AFTER_END_DATE'));
      return;
    }
    const payload: LocationDataProcessMeasure = {
      type: 'threshold',
      project_device_location: this.selectedLocation.id,
      id: this.generateThresholdId(start_date, end_date)
    };
    payload.start_date = start_date;
    payload.end_date = end_date;
    payload.value = this.thresholdValue;
    payload.thresholdLevel = {...this.thresholdLevel};
    this.updateThresholdDateDisplay(payload);
    this._thresholdTable.push(payload);
    this.thresholdValue = undefined;
    this.thresholdLevel = undefined;
    this.saveThresholdTable();
  }

  saveThresholdTable() {
    const graphicComponentView = this.localStorageService.getGraphicalComponentView(this.idProject, this.localStorageService.getCurrentIndex(this.idProject));
    graphicComponentView.thresholdTable = [...this._thresholdTable];
    this.thresholdTableChange.emit(graphicComponentView.thresholdTable);
    this.localStorageService.setGraphicalComponentViews(graphicComponentView, this.localStorageService.getCurrentIndex(this.idProject), this.idProject);
    this.toastr.success(this.translateService.instant('THRESHOLD_TABLE_SAVED'));
  }

  addFile() {
    this.selectedFile = this.fileInput.nativeElement.files[0];
  }

  generateThresholdId(start_date: string, end_date: string, index: number = 0) {
    return index + start_date + end_date + new Date().getTime();
  }

  sendThresholdJson(jsonFile: File) {
    this.fileService.getFileContent(jsonFile).then((jsonString) => {
      const result: LocationDataProcessMeasure[] = JSON.parse(jsonString);
      for (let i = 0; i < result.length; i++) {
        const threshold = result[i];
        if (!this.checkValidThreshold(threshold)) {
          return;
        }
        threshold.start_date = this.projectMeasureService.formatDateToDisplay(parseInt(threshold.start_date) * 1000);
        threshold.end_date = this.projectMeasureService.formatDateToDisplay(parseInt(threshold.end_date) * 1000);
        threshold.id = this.generateThresholdId(threshold.start_date, threshold.end_date, i + 1);
        threshold.type = 'threshold';
        threshold.thresholdLevel = this.thresholdLevels.find(t => t.id === <any>threshold.thresholdLevel);
      }
      this._thresholdTable = result;
      this.saveThresholdTable();
      this.toastr.success(this.translateService.instant('THRESHOLD_JSON_SENT'));
    }).catch((error) => console.log(error));
  }

  private checkValidThreshold(threshold: LocationDataProcessMeasure) {
    const locationsIds: number[] = this.locationsFormatterService.getAllLocationsFlat(this.locations).map(l => l.id);
    if (!locationsIds.includes(threshold.project_device_location)) {
      this.toastr.error(this.translateService.instant('THRESHOLD_JSON_DEVICE_LOCATION_ID_ERROR'));
      return false;
    }
    // Pour seulement ce cas la, on sait que thresholdLevel: string
    if (!this.thresholdLevels.map(l => l.id).includes(<any>threshold.thresholdLevel)) {
      this.toastr.error(this.translateService.instant('MUST_SELECT_LEVEL'));
      return false;
    }
    if (isNaN(threshold.value) || typeof threshold.value !== 'number') {
      this.toastr.error(this.translateService.instant('MUST_CHOOSE_VALUE'));
      return false;
    }
    if (parseInt(threshold.start_date) > parseInt(threshold.end_date)) {
      this.toastr.error(this.translateService.instant('START_DATE_AFTER_END_DATE'));
      return false;
    }
    return true;
  }

  getJsonThresholdFile() {
    const thresholds = cloneDeep(this._thresholdTable).map((t) => {
      t.start_date = moment.utc(t.start_date).unix().toString();
      t.end_date = moment.utc(t.end_date).unix().toString();
      t.thresholdLevel = t.thresholdLevel.id;
      delete t.id;
      delete t.selected;
      delete t.type;
      delete t.dateStartToDisplay;
      delete t.dateEndToDisplay;
      return t;
    });
    this.downloadArrayInJson(thresholds, 'thresholds');
  }

  getDefaultJsonThresholdFile() {
    const thresholdModel = <LocationDataProcessMeasure>{
      project_device_location: 1234,
      start_date: 'timestamp',
      end_date: 'timestamp',
      value: 0.1,
      thresholdLevel: <any>[...this.thresholdLevels].map(l => l.id).join(' | ')
    };
    this.downloadArrayInJson([thresholdModel], 'default_threshold_model');
  }

  downloadArrayInJson(arrayToDownload: any[], fileName: string) {
    const blob = new Blob([JSON.stringify(arrayToDownload, undefined, 3)], {type: 'application/json'});
    saveAs(blob, fileName);
  }
}
